import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Plus, Trash2, Edit3, X, Truck, Search, AlertTriangle, CheckCircle, Camera } from 'lucide-react';

function VehicleForm({ vehicle, onSave, onCancel }) {
  const [form, setForm] = useState(vehicle || {
    plate: '', brand: '', model: '', year: new Date().getFullYear(), type: 'carrinha',
    status: 'ativo', inspection_date: '', inspection_expiry: '', insurance_company: '',
    insurance_expiry: '', insurance_policy: '', km: 0, notes: '', assigned_project: ''
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  return (
    <div className="animate-in pt-10 lg:pt-0">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">{vehicle ? 'Editar Veiculo' : 'Novo Veiculo'}</h1>
        <button data-testid="close-vehicle-form" onClick={onCancel} className="btn btn-outline btn-sm"><X size={16} /></button>
      </div>
      <div className="pro-card-padded">
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Identificacao</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div><label className="field-label">Matricula</label><input data-testid="vehicle-plate" className="input" placeholder="AA-00-AA" value={form.plate} onChange={e => set('plate', e.target.value)} /></div>
          <div><label className="field-label">Marca</label><input data-testid="vehicle-brand" className="input" value={form.brand} onChange={e => set('brand', e.target.value)} /></div>
          <div><label className="field-label">Modelo</label><input data-testid="vehicle-model" className="input" value={form.model} onChange={e => set('model', e.target.value)} /></div>
          <div><label className="field-label">Ano</label><input className="input" type="number" value={form.year} onChange={e => set('year', parseInt(e.target.value) || 0)} /></div>
          <div><label className="field-label">Tipo</label>
            <select className="select" value={form.type} onChange={e => set('type', e.target.value)}>
              <option value="carrinha">Carrinha</option><option value="camiao">Camiao</option><option value="maquina">Maquina</option><option value="ligeiro">Ligeiro</option><option value="outro">Outro</option>
            </select></div>
          <div><label className="field-label">Estado</label>
            <select className="select" value={form.status} onChange={e => set('status', e.target.value)}>
              <option value="ativo">Ativo</option><option value="manutencao">Manutencao</option><option value="inativo">Inativo</option>
            </select></div>
        </div>
        <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-secondary)' }}>Inspecao & Seguro</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
          <div><label className="field-label">Data Inspecao</label><input className="input" type="date" value={form.inspection_date} onChange={e => set('inspection_date', e.target.value)} /></div>
          <div><label className="field-label">Validade Inspecao</label><input className="input" type="date" value={form.inspection_expiry} onChange={e => set('inspection_expiry', e.target.value)} /></div>
          <div><label className="field-label">KM</label><input className="input" type="number" value={form.km} onChange={e => set('km', parseInt(e.target.value) || 0)} /></div>
          <div><label className="field-label">Seguradora</label><input className="input" value={form.insurance_company} onChange={e => set('insurance_company', e.target.value)} /></div>
          <div><label className="field-label">Validade Seguro</label><input className="input" type="date" value={form.insurance_expiry} onChange={e => set('insurance_expiry', e.target.value)} /></div>
          <div><label className="field-label">Apolice</label><input className="input" value={form.insurance_policy} onChange={e => set('insurance_policy', e.target.value)} /></div>
        </div>
        <div><label className="field-label">Notas</label><textarea className="input" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
        <div className="flex gap-2 justify-end mt-4">
          <button data-testid="cancel-vehicle-btn" onClick={onCancel} className="btn btn-outline">Cancelar</button>
          <button data-testid="save-vehicle-btn" onClick={() => onSave(form)} className="btn btn-primary">Guardar</button>
        </div>
      </div>
    </div>
  );
}

function isExpired(d) { return d && new Date(d) < new Date(); }
function isExpiringSoon(d) { if (!d) return false; const diff = (new Date(d) - new Date()) / 86400000; return diff > 0 && diff <= 30; }

export default function FleetPage() {
  const [vehicles, setVehicles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState(null);
  const [photoCaption, setPhotoCaption] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');

  const load = useCallback(() => { api.get('/vehicles').then(r => setVehicles(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    if (editing) await api.put(`/vehicles/${editing.id}`, form);
    else await api.post('/vehicles', form);
    setEditing(null); setCreating(false); load();
  };

  const handleDelete = async (id) => { if (window.confirm('Eliminar?')) { await api.delete(`/vehicles/${id}`); load(); } };

  const addPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !viewing) return;
    const reader = new FileReader();
    reader.onload = async (ev) => {
      await api.post(`/vehicles/${viewing.id}/photos`, { image_base64: ev.target.result.split(',')[1], caption: photoCaption });
      setPhotoCaption('');
      const { data } = await api.get(`/vehicles/${viewing.id}`);
      setViewing(data);
    };
    reader.readAsDataURL(file);
  };

  const deletePhoto = async (phId) => {
    if (!viewing) return;
    await api.delete(`/vehicles/${viewing.id}/photos/${phId}`);
    const { data } = await api.get(`/vehicles/${viewing.id}`);
    setViewing(data);
  };

  const filtered = vehicles.filter(v => {
    if (filterStatus && v.status !== filterStatus) return false;
    if (filterType && v.type !== filterType) return false;
    if (search && ![v.plate, v.brand, v.model].some(f => f?.toLowerCase().includes(search.toLowerCase()))) return false;
    return true;
  });

  if (viewing) {
    return (
      <div className="animate-in pt-10 lg:pt-0">
        <button onClick={() => { setViewing(null); load(); }} className="text-xs font-medium cursor-pointer mb-1" style={{ color: 'var(--text-light)', background: 'none', border: 'none' }}>&#8592; Voltar</button>
        <h1 className="text-xl font-bold mb-4">{viewing.plate} - {viewing.brand} {viewing.model}</h1>
        <div className="pro-card-padded mb-4">
          <h3 className="text-sm font-semibold mb-3">Fotos do Veiculo</h3>
          <div className="flex gap-2 mb-3 items-end flex-wrap">
            <input className="input flex-1 min-w-[150px]" placeholder="Legenda" value={photoCaption} onChange={e => setPhotoCaption(e.target.value)} />
            <label className="btn btn-primary btn-sm cursor-pointer"><Camera size={14} /> Upload<input type="file" accept="image/*" className="hidden" onChange={addPhoto} /></label>
          </div>
          {viewing.photos?.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {viewing.photos.map(p => (
                <div key={p.id} className="pro-card overflow-hidden">
                  {p.image_base64 && <img src={`data:image/jpeg;base64,${p.image_base64}`} alt={p.caption} className="w-full h-28 object-cover" />}
                  <div className="p-2 flex items-center justify-between">
                    <p className="text-xs font-medium truncate">{p.caption || 'Sem legenda'}</p>
                    <button onClick={() => deletePhoto(p.id)} className="btn btn-danger btn-xs"><Trash2 size={10} /></button>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-xs" style={{ color: 'var(--text-light)' }}>Sem fotos</p>}
        </div>
      </div>
    );
  }

  if (creating || editing) return <VehicleForm vehicle={editing} onSave={handleSave} onCancel={() => { setEditing(null); setCreating(false); }} />;

  return (
    <div className="animate-in pt-10 lg:pt-0">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl font-bold">Gestao de Frota</h1>
          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{vehicles.length} veiculo{vehicles.length !== 1 ? 's' : ''}</p>
        </div>
        <button data-testid="new-vehicle-btn" onClick={() => setCreating(true)} className="btn btn-primary"><Plus size={16} /> Novo Veiculo</button>
      </div>
      <div className="mb-4 flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[150px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-light)' }} />
          <input data-testid="vehicle-search" className="input pl-9" placeholder="Pesquisar..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select data-testid="vehicle-filter-status" className="select" style={{ width: 'auto' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">Todos os estados</option>
          <option value="ativo">Ativo</option>
          <option value="manutencao">Manutencao</option>
          <option value="inativo">Inativo</option>
        </select>
        <select data-testid="vehicle-filter-type" className="select" style={{ width: 'auto' }} value={filterType} onChange={e => setFilterType(e.target.value)}>
          <option value="">Todos os tipos</option>
          <option value="carrinha">Carrinha</option>
          <option value="camiao">Camiao</option>
          <option value="maquina">Maquina</option>
          <option value="ligeiro">Ligeiro</option>
          <option value="outro">Outro</option>
        </select>
      </div>
      {filtered.length === 0 ? (
        <div className="pro-card-padded text-center py-12"><Truck size={40} className="mx-auto mb-3" style={{ color: 'var(--text-light)' }} /><p className="font-semibold">Sem veiculos</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map(v => (
            <div key={v.id} data-testid={`vehicle-card-${v.id}`} className="pro-card-padded">
              <div className="flex items-start justify-between mb-2">
                <div><h3 className="text-base font-bold">{v.plate}</h3><p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{v.brand} {v.model} ({v.year})</p></div>
                <span className={`badge ${v.status === 'ativo' ? 'badge-success' : v.status === 'manutencao' ? 'badge-warning' : 'badge-error'}`}>{v.status}</span>
              </div>
              <div className="space-y-1 mb-3 text-xs">
                {v.inspection_expiry && (
                  <div className="flex items-center gap-1.5">
                    {isExpired(v.inspection_expiry) ? <><AlertTriangle size={12} color="var(--error)" /><span style={{ color: 'var(--error)' }} className="font-medium">Inspecao expirada</span></> :
                     isExpiringSoon(v.inspection_expiry) ? <><AlertTriangle size={12} color="var(--warning)" /><span style={{ color: 'var(--warning)' }} className="font-medium">Inspecao a expirar</span></> :
                     <><CheckCircle size={12} color="var(--success)" /><span>Inspecao: {v.inspection_expiry}</span></>}
                  </div>
                )}
                {v.insurance_expiry && (
                  <div className="flex items-center gap-1.5">
                    {isExpired(v.insurance_expiry) ? <><AlertTriangle size={12} color="var(--error)" /><span style={{ color: 'var(--error)' }} className="font-medium">Seguro expirado</span></> :
                     isExpiringSoon(v.insurance_expiry) ? <><AlertTriangle size={12} color="var(--warning)" /><span style={{ color: 'var(--warning)' }} className="font-medium">Seguro a expirar</span></> :
                     <><CheckCircle size={12} color="var(--success)" /><span>Seguro: {v.insurance_expiry}</span></>}
                  </div>
                )}
                {v.km > 0 && <p className="font-medium">{v.km.toLocaleString()} km</p>}
              </div>
              <div className="flex gap-1">
                <button data-testid={`view-vehicle-${v.id}`} onClick={() => setViewing(v)} className="btn btn-outline btn-sm flex-1"><Camera size={12} /> Fotos</button>
                <button data-testid={`edit-vehicle-${v.id}`} onClick={() => setEditing(v)} className="btn btn-outline btn-sm flex-1"><Edit3 size={12} /> Editar</button>
                <button data-testid={`delete-vehicle-${v.id}`} onClick={() => handleDelete(v.id)} className="btn btn-danger btn-sm"><Trash2 size={12} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
